import { useEffect, useMemo, useState } from "react";
import { Sparkle, Package, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import BuildBundleModal from "./BuildBundleModal";
import { buildBundleCollage, collageGridClass } from "./bundleCollage";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "sws_bundles_v1";

/**
 * FeaturedBundlesStrip — top-of-Shop hero-style bundle grid.
 * Shows 3 featured bundles (configured in admin via `featured_slugs`) with a
 * "Shop All Bundles" button that expands the remaining bundles inline. Reuses
 * the same quote/build flow as `BundlesSection` (opens `BuildBundleModal`).
 */
export default function FeaturedBundlesStrip() {
  const readCached = () => {
    try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
  };
  const cached = readCached();
  const [data, setData] = useState(cached || { section: {}, bundles: [], catalog: {} });
  const [loading, setLoading] = useState(!cached);
  const [showAll, setShowAll] = useState(false);
  const [openBundle, setOpenBundle] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND}/api/bundles`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setData(d);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch { /* private-mode */ }
      })
      .catch((e) => console.warn("FeaturedBundlesStrip: fetch failed:", e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const { featured, extras } = useMemo(() => {
    const bundles = data?.bundles || [];
    const section = data?.section || {};
    let slugs = Array.isArray(section.featured_slugs) && section.featured_slugs.length
      ? section.featured_slugs
      : (section.featured_slug ? [section.featured_slug] : []);
    // Cap at 3 and fill from remaining bundles if fewer than 3 configured
    slugs = slugs.slice(0, 3);
    const bySlug = Object.fromEntries(bundles.map((b) => [b.slug, b]));
    const chosen = slugs.map((s) => bySlug[s]).filter(Boolean);
    if (chosen.length < 3) {
      for (const b of bundles) {
        if (chosen.length >= 3) break;
        if (!chosen.find((x) => x.slug === b.slug)) chosen.push(b);
      }
    }
    const chosenSlugs = new Set(chosen.map((b) => b.slug));
    const extras = bundles.filter((b) => !chosenSlugs.has(b.slug));
    return { featured: chosen, extras };
  }, [data]);

  if (loading && (data.bundles?.length ?? 0) === 0) {
    return (
      <section
        id="featured-legacy-bundles"
        data-testid="featured-bundles-strip-loading"
        className="border-b border-white/10 px-4 md:px-8 py-14 md:py-16 max-w-7xl mx-auto"
      >
        <p className="text-[#B32027] uppercase tracking-[0.35em] text-xs font-bold mb-3 flex items-center gap-2">
          <Sparkle size={12} weight="fill" /> / Featured Legacy Bundles
        </p>
        <h2 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl md:text-6xl mb-4">FEATURED LEGACY BUNDLES</h2>
        <p className="text-white/50 italic flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Loading bundles from Printful — first-load can take a few seconds…</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          {[0,1,2].map((i) => (
            <div key={i} className="aspect-[4/3] bg-[#141414] border border-white/10 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (data.section?.hidden) return null;
  if (featured.length === 0) {
    // Empty state — still render the anchor so the "Bundles" filter chip on the
    // Live Catalog has a valid scroll target. Without this, the chip becomes a
    // dead click when Printful is down or bundles haven't been seeded yet.
    return (
      <section
        id="featured-legacy-bundles"
        data-testid="featured-bundles-strip-empty"
        className="border-b border-white/10 px-4 md:px-8 py-12 max-w-7xl mx-auto"
      >
        <p className="text-[#B32027] uppercase tracking-[0.35em] text-xs font-bold mb-3">/ Featured Legacy Bundles</p>
        <p className="text-white/50 italic">Bundles are being restocked. Check back in a minute.</p>
      </section>
    );
  }

  const heading = data.section?.heading || "FEATURED LEGACY BUNDLES";
  const subheading = data.section?.subheading || "";
  const logo = data.section?.logo_url || "";

  const onAdded = (quote) => {
    toast.success(`${quote.bundle_name} added to cart — free sticker included`, {
      description: `${quote.line_items.length - (quote.line_items.some((li) => li.is_free_gift) ? 1 : 0)} items · $${quote.bundle_price.toFixed(2)}`,
    });
  };

  return (
    <section
      data-testid="featured-bundles-strip"
      id="featured-legacy-bundles"
      className="border-b border-white/10 px-4 md:px-8 pt-10 md:pt-14 pb-14 md:pb-20 max-w-7xl mx-auto"
    >
      <div className="mb-8 md:mb-10">
        <div className="flex items-start gap-4 flex-wrap">
          {logo && (
            <img src={logo} alt="Sweat for Sacrifice mark" className="h-14 md:h-16 w-auto object-contain" data-testid="featured-bundles-logo" />
          )}
          <div className="flex-1 min-w-[240px]">
            <p className="text-[#B32027] uppercase tracking-[0.35em] text-xs font-bold mb-3 flex items-center gap-2">
              <Sparkle size={12} weight="fill" /> / Featured Legacy Bundles
            </p>
            <h2 data-testid="featured-bundles-heading" className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl md:text-6xl">{heading}</h2>
            {subheading && <p className="text-white/75 text-sm md:text-base max-w-3xl leading-relaxed mt-3">{subheading}</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {featured.map((b) => (
          <BundleCard key={b.slug} bundle={b} catalog={data.catalog} sectionLogo={logo} onOpen={() => setOpenBundle(b)} />
        ))}
      </div>

      {extras.length > 0 && (
        <div className="mt-8 md:mt-10 flex flex-col items-center gap-4">
          <button
            type="button"
            data-testid="shop-all-bundles-btn"
            onClick={() => {
              setShowAll((v) => {
                const next = !v;
                if (next) {
                  // Wait one tick, then scroll the extras row into view.
                  setTimeout(() => {
                    const el = document.getElementById("all-bundles-grid");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 60);
                }
                return next;
              });
            }}
            className="inline-flex items-center gap-3 bg-[#B32027] hover:bg-[#D42730] text-white px-8 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
          >
            {showAll ? "Hide all bundles" : "Shop all bundles"} <ArrowRight size={12} weight="bold" className={showAll ? "rotate-90 transition-transform" : "transition-transform"} />
          </button>
          {showAll && (
            <div id="all-bundles-grid" data-testid="all-bundles-grid" className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
              {extras.map((b) => (
                <BundleCard key={b.slug} bundle={b} catalog={data.catalog} sectionLogo={logo} onOpen={() => setOpenBundle(b)} />
              ))}
            </div>
          )}
        </div>
      )}

      <p className="text-white/55 text-xs uppercase tracking-[0.2em] font-bold mt-8 flex items-center gap-2">
        <Package size={14} weight="bold" />
        Bundle products are made on demand and may arrive in separate packages.
      </p>

      {openBundle && (
        <BuildBundleModal bundle={openBundle} catalog={data.catalog} onClose={() => setOpenBundle(null)} onAdded={onAdded} />
      )}
    </section>
  );
}

/** BundleCard — copy of the same card used in BundlesSection, kept local so
 * this component is drop-in without breaking the existing full-section usage. */
function BundleCard({ bundle, catalog, sectionLogo, onOpen }) {
  const collage = buildBundleCollage(bundle, catalog);
  const cover = bundle.cover_image_url;
  const showLogoOnCard = bundle.slug === "og-legacy" && sectionLogo;

  let saveHint = "";
  if (bundle.mode === "lowest_free") {
    const pool = new Set();
    for (const slot of (bundle.slots || [])) for (const pid of (slot.eligible_products || [])) pool.add(pid);
    const lowest = Math.min(...Array.from(pool).map((pid) => {
      const p = catalog[pid] || catalog[String(pid)];
      return p?.price_from ?? 999;
    }));
    if (Number.isFinite(lowest)) saveHint = `Save up to $${Math.round(lowest)}`;
  } else if (bundle.price > 0) {
    let maxRetail = 0;
    for (const slot of (bundle.slots || [])) {
      let slotMax = 0;
      for (const pid of (slot.eligible_products || [])) {
        const p = catalog[pid] || catalog[String(pid)];
        if (p?.price_to && p.price_to > slotMax) slotMax = p.price_to;
      }
      maxRetail += slotMax;
    }
    const save = Math.max(0, Math.round(maxRetail - bundle.price));
    if (save > 0) saveHint = `Save up to $${save}`;
  }

  return (
    <article data-testid={`featured-bundle-card-${bundle.slug}`} className="border-2 border-white/15 hover:border-[#B32027] bg-[#0F0F0F] overflow-hidden transition-colors group flex flex-col">
      <div className="relative aspect-[4/3] bg-[#141414] overflow-hidden">
        {cover ? (
          <img src={cover} alt={bundle.name} className="w-full h-full object-cover" />
        ) : collage.length > 0 ? (
          <div data-testid={`featured-bundle-collage-${bundle.slug}`} data-collage-count={collage.length} className={`grid ${collageGridClass(collage.length)} gap-0.5 w-full h-full bg-black`}>
            {collage.map((src, i) => (
              <div key={src + i} className="bg-[#141414] overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">No preview</div>
        )}
        {showLogoOnCard && (
          <img src={sectionLogo} alt="Sweat for Sacrifice" className="absolute top-3 left-3 h-10 md:h-12 w-auto object-contain drop-shadow-lg" />
        )}
        {bundle.badge && (
          <span data-testid={`featured-bundle-badge-${bundle.slug}`} className="absolute top-3 right-3 bg-[#B32027] text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5">
            {bundle.badge}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1 gap-3">
        <h3 className="font-display font-black uppercase tracking-tighter text-xl md:text-2xl leading-[0.95]">{bundle.name}</h3>
        <div className="flex items-baseline gap-3 flex-wrap">
          {bundle.mode === "lowest_free" ? (
            <p className="text-white text-lg md:text-xl font-bold uppercase tracking-tight" data-testid={`featured-bundle-price-${bundle.slug}`}>Pick 5 · Lowest FREE</p>
          ) : (
            <p className="text-white text-2xl md:text-3xl font-bold" data-testid={`featured-bundle-price-${bundle.slug}`}>${bundle.price.toFixed(0)}</p>
          )}
          {saveHint && (
            <p data-testid={`featured-bundle-save-${bundle.slug}`} className="text-[#B32027] text-xs font-bold uppercase tracking-[0.2em]">{saveHint}</p>
          )}
        </div>
        {bundle.description && (
          <p className="text-white/70 text-sm leading-relaxed flex-1">{bundle.description}</p>
        )}
        <ul className="text-white/60 text-[11px] space-y-0.5">
          <li>+ Free 3-inch sticker</li>
          {bundle.coupon_code && (
            <li>Auto-applies <span className="font-mono text-[#B32027]">{bundle.coupon_code}</span> at checkout</li>
          )}
        </ul>
        <button
          type="button"
          data-testid={`featured-bundle-cta-${bundle.slug}`}
          onClick={onOpen}
          className="mt-2 inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
        >
          Build your bundle <ArrowRight size={12} weight="bold" />
        </button>
      </div>
    </article>
  );
}
